import { isNumber } from 'lodash';
import toInteger from 'lodash/toInteger';
import PptxGenJS from '@meitu/js-to-pptx';
import { createCanvas as createCanvasWasm } from '@meitu/wasm-lib';
import { getFramework, IFramework } from '@/framework';
import { getLocaleStore } from '@/i18n/index';
import { base64Kit } from '@/utils/base64-kit';
import {
    convertImageToWebp,
    convertWebpToImage,
    createCanvas,
    createForeignObjectImageUrl,
    downScaleCanvas,
    exportSize,
    fetchVectorText,
    getDrawContainParams,
    getPathname,
    imageConverter,
    loadImageAsync,
    serializerHtmlToString,
    serializeSvgText,
} from '@/utils/file-kit';
import { appendFilterId, deleteFilterPreview, fetchAlphaResult } from '@/utils/filter-kit';
import { deleteExtraInfo } from '@/utils/layer-kit';
import { pxToInch } from '@/utils/unit-kit';
import {
    GroupRoleType,
    IActiveBaseInfo,
    IExportBehavior,
    IGroupLayerProps,
    IImageLayerProps,
    IImageProps,
    ILayerBehavior,
    ILayerOriginInfo,
    ILayerProps,
    IMAGE_LAYER_CANVAS_ID_PREFIX,
    IRenderLayerExtraProps,
    ISize,
    LayerType,
    MaterialIdsType,
    MIN_IMG_SIZE,
    StretchOptionType,
} from '@/version-types';
import { applyEnhanceGLResult } from '../main-layer/actions/enhance';
import { getImageEffectResultImgSize } from './constants/image-layer-constant';
import ImageEffect from './utils/image-effect-action';
import { doImageEffect, drawBorderRadius } from './utils/layer';
import ImageLayer from './view/image-layer';

function getBehavior(identity: string) {
    function fixEffectData(data: IImageLayerProps) {
        // 修复特效丢失
        if (data.effect && (data.effect.glow || data.effect.stroke || data.effect.shadow)) {
            if (!isNumber(data.effect.effectAlpha)) {
                data.effect.effectAlpha = 1;
            }
            if (!data.effect.glow) {
                data.effect.glow = [];
            }
            if (!data.effect.stroke) {
                data.effect.stroke = [];
            }
            if (!data.effect.shadow) {
                data.effect.shadow = [];
            }
        }
    }

    const checkIsPuzzle = (layer: ILayerProps, framework: IFramework) => {
        const parentId = layer.parentId;
        if (!parentId) return false;
        const parentLayer = framework.getLayer(parentId);

        if (!parentLayer) return false;
        return (
            parentLayer.layerType === LayerType.GROUP_LAYER &&
            (parentLayer.data as IGroupLayerProps).extension?.role === GroupRoleType.PUZZLE
        );
    };

    const layerBehavior: ILayerBehavior = {
        render: (layer: ILayerProps, extraProps: IRenderLayerExtraProps) => {
            return <ImageLayer layer={layer} {...extraProps} />;
        },

        async savePPTLayerAsync(slide, layer, stageWidth, stageHeight) {
            const { left, top, width, height, rotate, data } = layer;
            const options: PptxGenJS.ImageProps = {
                // path: url,
                x: pxToInch(left),
                y: pxToInch(top),
                w: pxToInch(width),
                h: pxToInch(height),
                rotate: rotate > 0 ? rotate : 360 + rotate,
                data: '',
            };

            const canvas = await layerBehavior.getResultCanvasAsync?.(
                {
                    ...layer,
                    rotate: 0,
                },
                {
                    width: stageWidth,
                    height: stageHeight,
                },
            );

            if (!canvas) {
                return;
            }
            const { effect } = data as IImageLayerProps;
            if (effect?.screenshotUrl) {
                const imgEffectResultImgSize = getImageEffectResultImgSize(data as IImageLayerProps);
                let [x, y, w, h] = [
                    left - (width * imgEffectResultImgSize - width) / 2,
                    top - (height * imgEffectResultImgSize - height) / 2,
                    width * imgEffectResultImgSize,
                    height * imgEffectResultImgSize,
                ];
                if (effect.effectPosition) {
                    x = left + effect.effectPosition.left;
                    y = top + effect.effectPosition.top;
                    w = effect.effectPosition.right - effect.effectPosition.left;
                    h = effect.effectPosition.bottom - effect.effectPosition.top;
                }

                const resultCanvas = createCanvasWasm(w, h);
                const ctx = resultCanvas.getContext('2d');
                if (ctx) {
                    ctx.globalAlpha = layer.opacity;
                }
                ctx?.drawImage(canvas, x, y, w, h, 0, 0, w, h);
                options.x = pxToInch(x);
                options.y = pxToInch(y);
                options.w = pxToInch(w);
                options.h = pxToInch(h);
                options.data = resultCanvas?.toDataURL() || '';
            } else {
                const resultCanvas = createCanvasWasm(width, height);
                const ctx = resultCanvas.getContext('2d');
                ctx?.drawImage(canvas, left, top, width, height, 0, 0, width, height);
                options.data = resultCanvas?.toDataURL() || '';
            }

            slide.addImage(options);
        },

        viewNeedBasicOperation() {
            return true;
        },

        onBeforeAddOrUpdate(layer: ILayerProps) {
            const data = layer.data as IImageLayerProps;
            // console.log('onBeforeAddOrUpdate 1=>', data.url);
            data.url = base64Kit.getCode(convertImageToWebp(data.url));
            // console.log('onBeforeAddOrUpdate 2=>', data.url);
            fixEffectData(data);
        },

        async onBeforeSaveAsync(layer: ILayerProps, isAutoSave?: boolean) {
            const data = layer.data as IImageLayerProps;
            deleteExtraInfo(layer);
            data.url = convertWebpToImage(data.url);
            data.maskURL = convertWebpToImage(data.maskURL);
            data.cutoutMaskURL = convertWebpToImage(data.cutoutMaskURL);
            // 获取变形文字的缩略图
            await layerBehavior.convertCodeToHttpUrl?.(layer);

            // 获取数据时，删除滤镜预览数据
            const filterID = deleteFilterPreview(layer);
            if (filterID) {
                // 将预览滤镜数据，默认应用到图层
                appendFilterId(layer, filterID, true);
            }
            // 获取预览滤镜的图片数据，执行上传
            const alphaResult = fetchAlphaResult(layer.id);
            if (alphaResult.base64) {
                data.url = base64Kit.getCode(alphaResult.base64, alphaResult.forceMd5);
            }
            // 应用gl渲染的调整结果
            await applyEnhanceGLResult(layer);
            if (data.url) {
                data.url = await base64Kit.getRemoteUrlAsync(data.url);
            }

            fixEffectData(data);
        },
        async getResultCanvasAsync(
            layer: ILayerProps,
            resultSize: ISize,
            includeEffect = true,
        ): Promise<HTMLCanvasElement | undefined> {
            // console.time('===========生成时间');
            try {
                const { width, height, left, top, opacity, rotate } = layer;
                const { width: stageWidth, height: stageHeight } = resultSize;
                const resultWidth = toInteger(width);
                const resultHeight = toInteger(height);
                const centerPoint = {
                    x: resultWidth / 2 + left,
                    y: resultHeight / 2 + top,
                };
                const drawOffsetPoint = {
                    x: -resultWidth / 2,
                    y: -resultHeight / 2,
                };
                const layerImage = await layerBehavior.getLayerImageAsync?.(layer, false);
                if (!layerImage) return;
                const { canvas: destCanvas, ctx: destCtx } = createCanvas(stageWidth, stageHeight);
                destCtx.translate(centerPoint.x, centerPoint.y);
                destCtx.rotate((Math.PI / 180) * rotate);

                if (includeEffect) {
                    const effectCanvas = await layerBehavior.getLayerEffectImageAsync?.(layer, true);

                    if (effectCanvas) {
                        let origin = [-effectCanvas.width / 2, -effectCanvas.height / 2];
                        const { effect } = layer.data as IImageLayerProps;
                        if (effect?.effectPosition) {
                            origin = [
                                drawOffsetPoint.x + effect.effectPosition.left,
                                drawOffsetPoint.y + effect.effectPosition.top,
                            ];
                        }
                        destCtx.drawImage(
                            effectCanvas,
                            0,
                            0,
                            effectCanvas.width,
                            effectCanvas.height,
                            origin[0],
                            origin[1],
                            effectCanvas.width,
                            effectCanvas.height,
                        );
                    }
                }

                // 透明度
                destCtx.globalAlpha = opacity;

                destCtx.drawImage(
                    layerImage,
                    0,
                    0,
                    resultWidth,
                    resultHeight,
                    drawOffsetPoint.x,
                    drawOffsetPoint.y,
                    resultWidth,
                    resultHeight,
                );
                // console.timeEnd('===========生成时间');
                return destCanvas;
            } catch (error) {
                return undefined;
            }
        },

        async getLayerEffectImageAsync(layer: ILayerProps, setOpacity = false) {
            const { data, width, height } = layer;
            const { effect } = data as IImageLayerProps;
            if (!effect || ImageEffect.isEmptyEffect(data as IImageLayerProps) || !effect.screenshotUrl) {
                return null;
            }
            const imgEffectResultImgSize = getImageEffectResultImgSize(data as IImageLayerProps);
            const { screenshotUrl } = effect;
            const canvas = document.createElement('canvas');
            if (effect.effectPosition) {
                canvas.width = effect.effectPosition.right - effect.effectPosition.left;
                canvas.height = effect.effectPosition.bottom - effect.effectPosition.top;
            } else {
                canvas.width = width * imgEffectResultImgSize;
                canvas.height = height * imgEffectResultImgSize;
            }
            const ctx = canvas.getContext('2d');
            if (ctx && setOpacity) {
                ctx.globalAlpha = layer.opacity;
            }
            const url = await base64Kit.getRemoteUrlAsync(screenshotUrl);
            const img = await loadImageAsync(url);

            ctx?.drawImage(img, 0, 0, img.width, img.height, 0, 0, canvas.width, canvas.height);
            return canvas;
        },

        // needScaleCanvas 当原图超过一定大小的时候是否压缩
        async getLayerImageAsync(
            layer: ILayerProps,
            globalAlpha = true,
            needScaleCanvas = true,
            noNeedBorderRadius = false,
            useLayerOpacity = true,
        ) {
            // console.time('===========生成时间');
            const { width, height, opacity } = layer;
            const {
                url,
                maskURL,
                cutoutMaskURL,
                imageTransform: { flipH, flipV, scale, rotate: imgRotate },
                naturalWidth: domWidth,
                minimumMask,
                borderRadius,
            } = layer.data as IImageLayerProps;

            let {
                widthRatio,
                heightRatio,
                offsetX: offsetXRatio,
                offsetY: offsetYRatio,
            } = (layer.data as IImageLayerProps).imageTransform;

            if (isNaN(widthRatio) || widthRatio === null) {
                widthRatio = 1;
            }

            if (isNaN(heightRatio) || heightRatio === null) {
                heightRatio = 1;
            }

            if (isNaN(offsetXRatio)) {
                offsetXRatio = 0;
            }

            if (isNaN(offsetYRatio)) {
                offsetYRatio = 0;
            }

            let baseImage: HTMLCanvasElement | HTMLImageElement = document.getElementById(
                `${IMAGE_LAYER_CANVAS_ID_PREFIX}${layer.id}`,
            ) as HTMLCanvasElement;

            // 等待上传完成
            await base64Kit.getRemoteUrlAsync(url);

            // 设置绘制的真正元素
            if (!baseImage || baseImage?.getAttribute('ready') === 'false') {
                try {
                    baseImage = await loadImageAsync(url);
                    const { canvas } = createCanvas(baseImage.width, baseImage.height, baseImage);
                    baseImage = canvas;
                } catch (e) {
                    const framework = getFramework(identity);

                    framework.events.emitBehaviorErrorImage({ layerId: layer.id, imageUrl: url });

                    return createCanvas(1, 1).canvas;
                }
            }

            const scaleCanvas = parseFloat(Math.floor(width / domWidth).toFixed(2));
            if (needScaleCanvas && scaleCanvas < 0.5 && widthRatio === 1 && heightRatio === 1 && scale === 1) {
                //没有使用过裁剪功能
                console.time(`图片压缩处理${layer.id}`);
                baseImage = downScaleCanvas(baseImage as HTMLCanvasElement, width, height);
                console.timeEnd(`图片压缩处理${layer.id}`);
            }

            const resultWidth = toInteger(width);
            const resultHeight = toInteger(height);
            const offsetX = toInteger(offsetXRatio * widthRatio * width * scale);
            const offsetY = toInteger(offsetYRatio * heightRatio * height * scale);
            const originImgWidth = toInteger(width * widthRatio);
            const originImgHeight = toInteger(height * heightRatio);
            let imgOffsetX = resultWidth / 2;
            let imgOffsetY = resultHeight / 2;
            if (flipH) {
                imgOffsetX = imgOffsetX - offsetX;
            } else {
                imgOffsetX = imgOffsetX + offsetX;
            }
            if (flipV) {
                imgOffsetY = imgOffsetY - offsetY;
            } else {
                imgOffsetY = imgOffsetY + offsetY;
            }
            // 将图层绘制到画布上
            const getLayerResult = async (layerCanvas: HTMLCanvasElement) => {
                // 绘制蒙版
                const { canvas: maskCanvas, ctx: maskCtx } = createCanvas(resultWidth, resultHeight);
                if (maskURL) {
                    let finalUrl = maskURL;
                    if (/\.svg$/.test(getPathname(maskURL) ?? '')) {
                        const svgMaskText = await fetchVectorText(maskURL);
                        const { width, height } = exportSize(svgMaskText);
                        console.log('svgMaskText===', minimumMask, width, height, svgMaskText);
                        finalUrl = createForeignObjectImageUrl(
                            Math.floor(width),
                            Math.floor(height),
                            serializerHtmlToString(serializeSvgText(svgMaskText, resultWidth, resultHeight)),
                            true,
                            '',

                            resultWidth,
                            resultHeight,
                        );

                        // console.log('finalUrl===', finalUrl);
                    }
                    const maskImg = await loadImageAsync(finalUrl);
                    if (minimumMask) {
                        maskCtx.drawImage(maskImg, 0, 0, resultWidth, resultHeight);
                    } else {
                        const res = getDrawContainParams(maskImg.width, maskImg.height, resultWidth, resultHeight);
                        maskCtx.drawImage(maskImg, res.dx, res.dy, res.dw, res.dh);
                    }
                    maskCtx.globalCompositeOperation = 'source-in';
                    maskCtx.drawImage(layerCanvas, 0, 0);
                }
                const finalCanvas = maskURL ? maskCanvas : layerCanvas;
                if (!globalAlpha) return finalCanvas;
                const { canvas: destCanvas, ctx: destCtx } = createCanvas(finalCanvas.width, finalCanvas.height);
                if (useLayerOpacity) {
                    destCtx.globalAlpha = opacity;
                }
                destCtx.drawImage(finalCanvas, 0, 0, finalCanvas.width, finalCanvas.height);
                return destCanvas;
            };

            //  绘制图层
            const getLayerCanvas = async () => {
                const { canvas: destCanvas, ctx: destCtx } = createCanvas(resultWidth, resultHeight);
                destCtx.save();
                destCtx.translate(imgOffsetX, imgOffsetY);

                let rotate = imgRotate;

                if (flipH) {
                    rotate = -rotate;
                }

                if (flipV) {
                    rotate = -rotate;
                }

                destCtx.rotate((rotate * Math.PI) / 180);
                destCtx.scale(scale * (flipH ? -1 : 1), scale * (flipV ? -1 : 1));
                destCtx.drawImage(
                    cutoutMaskResult || baseImage,
                    -originImgWidth / 2,
                    -originImgHeight / 2,
                    originImgWidth,
                    originImgHeight,
                );
                destCtx.restore();
                return destCanvas;
            };

            let cutoutMaskResult: HTMLCanvasElement | null = null;
            if (cutoutMaskURL) {
                // 抠图模板融合
                const { canvas: maskCanvas, ctx: maskCtx } = createCanvas(baseImage.width, baseImage.height);
                const cutoutMaskURLImg = await loadImageAsync(cutoutMaskURL);
                maskCtx.drawImage(baseImage, 0, 0);
                maskCtx.globalCompositeOperation = 'destination-in';
                maskCtx.drawImage(cutoutMaskURLImg, 0, 0, baseImage.width, baseImage.height);
                cutoutMaskResult = maskCanvas;
            }

            const layerCanvas = await getLayerCanvas();

            const result = await getLayerResult(layerCanvas);

            if (borderRadius && !borderRadius.hidden && !noNeedBorderRadius) {
                drawBorderRadius(result, borderRadius, 0, 0);
            }

            return result;
        },

        getActiveBaseInfo(layer: ILayerProps): IActiveBaseInfo {
            const data = layer.data as IImageLayerProps;
            const { naturalHeight, naturalWidth } = data;
            return {
                cutWidth: Math.min(naturalWidth, MIN_IMG_SIZE),
                cutHeight: Math.min(naturalHeight, MIN_IMG_SIZE),
            };
        },
        getScaleMinRatio(originInfo: ILayerOriginInfo, layer: ILayerProps): number {
            const { width, cutWidth } = originInfo;
            const offWidth = width - cutWidth;
            return (width - offWidth) / width;
        },
        getScaleIsNeedLimitSize(): boolean {
            return true;
        },

        whenGroupCanResize(layer: ILayerProps) {
            const { effect, borderRadius } = layer.data as IImageLayerProps;
            let canResize = !effect && !borderRadius;

            if (checkIsPuzzle(layer, getFramework(identity))) {
                canResize = true;
            }

            return {
                horizontal: canResize,
                vertical: canResize,
            };
        },

        onBeforeResizeUpdate(layer: ILayerProps) {
            const { imageTransform, naturalWidth, naturalHeight, stretchOption } = layer.data as IImageLayerProps;

            if (stretchOption === StretchOptionType.cover) {
                // 智能模式 每次用原始图片和当前图层大小算出比例
                const newWidthRatio = naturalWidth / layer.width;
                const newHeightRatio = naturalHeight / layer.height;

                const ratio = newWidthRatio < newHeightRatio ? newWidthRatio : newHeightRatio;

                imageTransform.widthRatio = naturalWidth / ratio / layer.width;
                imageTransform.heightRatio = naturalHeight / ratio / layer.height;
            }
        },

        checkIsPhoto(layer: ILayerProps) {
            return !!layer?.materialIds?.[MaterialIdsType.PHOTO_MATERIAL]?.length;
        },

        checkIsAiText(layer: ILayerProps) {
            return !!(layer?.data as IImageLayerProps)?.aiTextExtension;
        },

        async convertCodeToHttpUrl(layer: ILayerProps) {
            const data = layer.data as IImageLayerProps;
            if (data.effect) {
                data.effect.screenshotUrl = await base64Kit.getRemoteUrlAsync(data.effect.screenshotUrl);
            } else if (data.url) {
                data.url = await base64Kit.getRemoteUrlAsync(data.url);
            }
        },

        getMenuOverlapName(layer: ILayerProps) {
            const { $t } = getLocaleStore();
            return $t((v) => v.layers.image);
            return '';
        },

        getMenuOverlapIcon(layer: ILayerProps) {
            const { url } = layer.data as IImageLayerProps;
            return (
                <div className="l-layer__icon border">
                    <img src={imageConverter(base64Kit.getLocalUrl(url), 32, 100)} alt="" />
                </div>
            );
        },

        async onBaseResizeEnd(layerId: string) {
            return doImageEffect(identity, layerId);
        },

        async onBaseScaleEnd(layerId: string) {
            return doImageEffect(identity, layerId);
        },

        onBeforeScaleUpdate(layer, { originInfo }) {
            const { effect } = layer.data as IImageLayerProps;
            if (!originInfo || !effect?.effectPosition) return;
            const { width: oldWidth, height: oldHeight } = originInfo;
            const { width, height } = layer;
            const scaleX = width / oldWidth;
            const scaleY = height / oldHeight;
            const { left, top, right, bottom } = effect.effectPosition;
            effect.effectPosition.left = left * scaleX;
            effect.effectPosition.top = top * scaleY;
            effect.effectPosition.right = right * scaleX;
            effect.effectPosition.bottom = bottom * scaleY;
        },

        checkDisableStretch(layer: ILayerProps) {
            return (layer.data as IImageProps)?.stretchOption === StretchOptionType.none;
        },
    };
    return layerBehavior;
}

const exportBehavior: IExportBehavior = {
    layerType: LayerType.IMAGE_LAYER,
    getBehavior,
};

export default exportBehavior;
